import { useMemo, useState } from "react";
import {
  Button,
  IconButton,
  NumberInput,
  TextInput,
  ToggleInput,
} from "@a2z/ui";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import { ModalSheet } from "@/shared/components/dialogs/ModalSheet";

export type AddVariantSizeColor = {
  size: string;
  sizeAr?: string;
  color: string;
  colorAr?: string;
  hex: string;
  price: number;
  discountedPrice: number | null;
  cost: number | null;
  stockCount: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  currency: string;
  existing: { size: string; color: string }[];
  defaultPrice: number;
  onAdd: (rows: AddVariantSizeColor[]) => Promise<void> | void;
};

const SIZE_PRESETS = ["XS", "S", "M", "L", "XL", "XXL"];

type ColorPreset = { en: string; ar: string; hex: string };
const COLOR_PRESETS: ColorPreset[] = [
  { en: "Black", ar: "أسود", hex: "#1a1a1a" },
  { en: "White", ar: "أبيض", hex: "#ffffff" },
  { en: "Red", ar: "أحمر", hex: "#ef4444" },
  { en: "Blue", ar: "أزرق", hex: "#3b82f6" },
  { en: "Green", ar: "أخضر", hex: "#16a34a" },
  { en: "Beige", ar: "بيج", hex: "#e8dcc4" },
  { en: "Grey", ar: "رمادي", hex: "#9ca3af" },
  { en: "Navy", ar: "كحلي", hex: "#1e3a5f" },
];

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

type ColorDraft = { en: string; ar: string; hex: string };

function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function CloseGlyph() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M6 18L18 6" />
    </svg>
  );
}

export function ProductAddVariantModal({
  isOpen,
  onClose,
  productName,
  currency,
  existing,
  defaultPrice,
  onAdd,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const presetLabel = (p: ColorPreset) => pickLocalized(locale, p.ar, p.en);
  const [mode, setMode] = useState<"single" | "bulk">("single");
  const [submitting, setSubmitting] = useState(false);

  // single mode
  const [size, setSize] = useState("");
  const [color, setColor] = useState<ColorDraft>({
    en: "",
    ar: "",
    hex: "#888888",
  });
  const [price, setPrice] = useState(String(defaultPrice));
  const [cost, setCost] = useState("");
  const [discountOn, setDiscountOn] = useState(false);
  const [discount, setDiscount] = useState("");
  const [stock, setStock] = useState("0");

  // bulk mode
  const [sizes, setSizes] = useState<string[]>(["S", "M", "L"]);
  const [colors, setColors] = useState<ColorDraft[]>([COLOR_PRESETS[0]]);
  const [newSize, setNewSize] = useState("");
  const [newColor, setNewColor] = useState<ColorDraft>({
    en: "",
    ar: "",
    hex: "#888888",
  });

  // Reset the form during render when the modal opens (or defaultPrice changes
  // while open) — previous-value comparison instead of a setState-in-effect.
  const [prevReset, setPrevReset] = useState({ isOpen, defaultPrice });
  if (prevReset.isOpen !== isOpen || prevReset.defaultPrice !== defaultPrice) {
    setPrevReset({ isOpen, defaultPrice });
    if (isOpen) {
      setMode("single");
      setSize("");
      setColor({ en: "", ar: "", hex: "#888888" });
      setPrice(String(defaultPrice));
      setCost("");
      setDiscountOn(false);
      setDiscount("");
      setStock("0");
      setSizes(["S", "M", "L"]);
      setColors([COLOR_PRESETS[0]]);
      setNewSize("");
      setNewColor({ en: "", ar: "", hex: "#888888" });
      setSubmitting(false);
    }
  }

  const existsKey = (s: string, c: string) =>
    existing.some(
      (v) =>
        v.size.toLowerCase() === s.toLowerCase() &&
        v.color.toLowerCase() === c.toLowerCase(),
    );

  const combos = useMemo(
    () =>
      sizes.flatMap((s) =>
        colors.map((c) => ({ size: s, color: c, exists: existsKey(s, c.en) })),
      ),
    [sizes, colors, existing],
  );
  const newCount = combos.filter((c) => !c.exists).length;
  const singleReady =
    mode === "single" &&
    Boolean(size) &&
    Boolean(color.en.trim()) &&
    !existsKey(size, color.en);
  const bulkReady = mode === "bulk" && newCount > 0;
  const canSubmit = (singleReady || bulkReady) && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const priceNum = Number(price) || 0;
      const stockNum = Number(stock) || 0;
      const trimmedCost = cost.trim();
      const costParsed = trimmedCost === "" ? null : Number(trimmedCost);
      // Seller-only goods cost — kept only when it's a valid non-negative number.
      const costValue =
        costParsed != null && Number.isFinite(costParsed) && costParsed >= 0
          ? costParsed
          : null;
      const trimmedDiscount = discount.trim();
      const discountNum =
        trimmedDiscount === "" ? null : Number(trimmedDiscount);
      // Only when the sale toggle is on AND the value is valid (below price).
      const discountedPrice =
        discountOn &&
        discountNum != null &&
        Number.isFinite(discountNum) &&
        discountNum > 0 &&
        discountNum < priceNum
          ? discountNum
          : null;
      if (mode === "single") {
        await onAdd([
          {
            size,
            sizeAr: size,
            color: color.en.trim(),
            colorAr: color.ar.trim() || color.en.trim(),
            hex: HEX_RE.test(color.hex) ? color.hex : "#888888",
            price: priceNum,
            discountedPrice,
            cost: costValue,
            stockCount: stockNum,
          },
        ]);
      } else {
        const rows = combos
          .filter((c) => !c.exists)
          .map((c) => ({
            size: c.size,
            sizeAr: c.size,
            color: c.color.en.trim(),
            colorAr: c.color.ar.trim() || c.color.en.trim(),
            hex: HEX_RE.test(c.color.hex) ? c.color.hex : "#888888",
            price: priceNum,
            discountedPrice,
            cost: costValue,
            stockCount: stockNum,
          }));
        if (rows.length) await onAdd(rows);
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel =
    mode === "single"
      ? t(keys.products.variantsAddVariant)
      : t(keys.products.addNVariants, { count: newCount });
  const footerNote =
    mode === "single"
      ? singleReady
        ? t(keys.products.oneVariantReady)
        : t(keys.products.pickSizeColor)
      : t(keys.products.nNewVariantsWillBeCreated, { count: newCount });

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="lg">
      <div className="flex max-h-[88vh] flex-col">
        <div className="shrink-0 border-b border-[color:var(--border-light)] px-5 pb-3 pt-1.5">
          <h2 className="text-[16px] font-semibold text-[color:var(--text-primary)]">
            {t(keys.products.variantsAddVariant)}
          </h2>
          <p className="mt-0.5 text-[13px] text-[color:var(--text-muted)]">
            {t(keys.products.variantAddSubtitle, { name: productName })}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Mode tabs */}
          <div className="mb-4 grid grid-cols-2 gap-2">
            {(
              [
                {
                  v: "single",
                  l: t(keys.products.variantModeSingleLabel),
                  h: t(keys.products.variantModeSingleHint),
                },
                {
                  v: "bulk",
                  l: t(keys.products.variantModeBulkLabel),
                  h: t(keys.products.variantModeBulkHint),
                },
              ] as const
            ).map((m) => {
              const activeTab = m.v === mode;
              return (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => setMode(m.v)}
                  className={[
                    "flex flex-col gap-0.5 rounded-lg border px-3.5 py-2.5 text-start transition-colors",
                    activeTab
                      ? "border-[color:var(--accent-500)] bg-[color-mix(in_oklab,var(--accent-500)_8%,white)]"
                      : "border-[color:var(--border-light)] bg-[color:var(--background-muted)] hover:bg-[color:var(--surface)]",
                  ].join(" ")}
                >
                  <span
                    className={`text-[13px] font-semibold leading-tight ${activeTab ? "text-[color:var(--accent-600)]" : "text-[color:var(--text-primary)]"}`}
                  >
                    {m.l}
                  </span>
                  <span className="text-[12px] leading-tight text-[color:var(--text-muted)]">
                    {m.h}
                  </span>
                </button>
              );
            })}
          </div>

          {mode === "single" ? (
            <div className="flex flex-col gap-4">
              {/* Size */}
              <Field label={t(keys.products.sizeLabel)}>
                <div className="flex flex-wrap items-center gap-1.5">
                  {SIZE_PRESETS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSize(s)}
                      className={chipClass(size === s)}
                    >
                      {s}
                    </button>
                  ))}
                  <input
                    value={SIZE_PRESETS.includes(size) ? "" : size}
                    onChange={(e) => setSize(e.target.value.toUpperCase())}
                    placeholder={t(keys.products.addSizePlaceholder)}
                    className="h-8 w-[110px] rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] px-2.5 font-mono text-[13px] focus:outline-none"
                  />
                </div>
              </Field>

              {/* Color */}
              <Field label={t(keys.products.colorLabel)}>
                <span className="mb-1.5 block text-[12px] text-[color:var(--text-muted)]">
                  {t(keys.products.quickColorsLabel)}
                </span>
                <div className="mb-2.5 flex flex-wrap gap-1.5">
                  {COLOR_PRESETS.map((p) => (
                    <button
                      key={p.en}
                      type="button"
                      onClick={() =>
                        setColor({ en: p.en, ar: p.ar, hex: p.hex })
                      }
                      className={[
                        "inline-flex items-center gap-1.5 rounded-full border py-1 ps-1 pe-2.5 text-[13px] font-medium transition-colors",
                        color.en === p.en
                          ? "border-[color:var(--accent-500)] bg-[color-mix(in_oklab,var(--accent-500)_10%,white)] text-[color:var(--accent-600)]"
                          : "border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--background-muted)]",
                      ].join(" ")}
                    >
                      <Swatch hex={p.hex} />
                      {presetLabel(p)}
                    </button>
                  ))}
                </div>
                <ColorEditor
                  draft={color}
                  onChange={setColor}
                  enLabel={t(keys.products.colorNameEn)}
                  arLabel={t(keys.products.colorNameAr)}
                  hint={t(keys.products.colorHexHint)}
                />
              </Field>

              {/* Price + stock */}
              <div className="grid grid-cols-2 gap-3">
                <Field label={t(keys.products.priceWithCurrency, { currency })}>
                  <NumberInput
                    name="price"
                    allowDecimal
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="font-mono font-semibold"
                  />
                </Field>
                <Field label={t(keys.products.stockLabel)}>
                  <NumberInput
                    name="stock"
                    value={stock}
                    onChange={(e) => setStock(e.target.value)}
                    className="font-mono font-semibold"
                  />
                </Field>
              </div>
              <Field
                label={`${t(keys.products.variantCostLabel)} (${currency})`}
              >
                <NumberInput
                  name="cost"
                  allowDecimal
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  placeholder="—"
                  className="font-mono font-semibold"
                />
                <span className="mt-1 block text-[11px] text-[color:var(--text-muted)]">
                  {t(keys.products.variantCostHint)}
                </span>
              </Field>
              <SaleToggle
                on={discountOn}
                setOn={setDiscountOn}
                value={discount}
                setValue={setDiscount}
                currency={currency}
                t={t}
              />

              {size && color.en && existsKey(size, color.en) ? (
                <p className="rounded-md border border-[color:var(--error-100)] bg-[color:var(--error-50)] px-3 py-2 text-[13px] text-[color:var(--error-500)]">
                  {t(keys.products.variantExists, {
                    combo: `${color.en} · ${size}`,
                  })}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Sizes */}
              <Field
                label={t(keys.products.sizesWithCount, { count: sizes.length })}
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  {sizes.map((s) => (
                    <Tag
                      key={s}
                      onRemove={() => setSizes(sizes.filter((x) => x !== s))}
                      removeLabel={t(keys.products.removeValueAria, {
                        value: s,
                      })}
                    >
                      <span className="font-mono font-semibold">{s}</span>
                    </Tag>
                  ))}
                  <input
                    value={newSize}
                    onChange={(e) => setNewSize(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const v = newSize.trim();
                        if (v && !sizes.includes(v)) setSizes([...sizes, v]);
                        setNewSize("");
                      }
                    }}
                    placeholder={t(keys.products.addSizePlaceholder)}
                    className="h-8 w-[110px] rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] px-2.5 font-mono text-[13px] focus:outline-none"
                  />
                </div>
              </Field>

              {/* Colors */}
              <Field
                label={t(keys.products.colorsWithCount, {
                  count: colors.length,
                })}
              >
                <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
                  {colors.map((c) => (
                    <Tag
                      key={c.en}
                      onRemove={() =>
                        setColors(colors.filter((x) => x.en !== c.en))
                      }
                      removeLabel={t(keys.products.removeValueAria, {
                        value: c.en,
                      })}
                    >
                      <Swatch hex={c.hex} />
                      <span>{c.en}</span>
                      {c.ar ? (
                        <span className="text-[color:var(--text-muted)]">
                          · {c.ar}
                        </span>
                      ) : null}
                    </Tag>
                  ))}
                </div>
                <div className="rounded-lg border border-dashed border-[color:var(--border-light)] p-2.5">
                  <ColorEditor
                    draft={newColor}
                    onChange={setNewColor}
                    enLabel={t(keys.products.colorNameEn)}
                    arLabel={t(keys.products.colorNameAr)}
                    hint={t(keys.products.colorHexHint)}
                  />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {COLOR_PRESETS.map((p) => (
                      <button
                        key={p.en}
                        type="button"
                        onClick={() =>
                          setNewColor({ en: p.en, ar: p.ar, hex: p.hex })
                        }
                        className="inline-flex items-center gap-1 rounded-full border border-[color:var(--border-light)] bg-[color:var(--surface)] py-0.5 ps-0.5 pe-2 text-[12px] text-[color:var(--text-secondary)] hover:bg-[color:var(--background-muted)]"
                      >
                        <Swatch hex={p.hex} />
                        {presetLabel(p)}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      leadingIcon={<PlusIcon />}
                      disabled={
                        !newColor.en.trim() ||
                        colors.some((c) => c.en === newColor.en.trim())
                      }
                      onClick={() => {
                        const en = newColor.en.trim();
                        if (!en || colors.some((c) => c.en === en)) return;
                        setColors([
                          ...colors,
                          { en, ar: newColor.ar.trim(), hex: newColor.hex },
                        ]);
                        setNewColor({ en: "", ar: "", hex: "#888888" });
                      }}
                    >
                      {t(keys.products.addColorAction)}
                    </Button>
                  </div>
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field
                  label={t(keys.products.defaultPriceWithCurrency, {
                    currency,
                  })}
                >
                  <NumberInput
                    name="bprice"
                    allowDecimal
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="font-mono font-semibold"
                  />
                </Field>
                <Field label={t(keys.products.defaultStockLabel)}>
                  <NumberInput
                    name="bstock"
                    value={stock}
                    onChange={(e) => setStock(e.target.value)}
                    className="font-mono font-semibold"
                  />
                </Field>
              </div>
              <Field
                label={`${t(keys.products.variantCostLabel)} (${currency})`}
              >
                <NumberInput
                  name="bcost"
                  allowDecimal
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  placeholder="—"
                  className="font-mono font-semibold"
                />
                <span className="mt-1 block text-[11px] text-[color:var(--text-muted)]">
                  {t(keys.products.variantCostHint)}
                </span>
              </Field>
              <SaleToggle
                on={discountOn}
                setOn={setDiscountOn}
                value={discount}
                setValue={setDiscount}
                currency={currency}
                t={t}
              />

              {/* Preview */}
              <Field
                label={t(keys.products.previewCombinations, {
                  count: combos.length,
                })}
              >
                <div className="grid max-h-[160px] grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-1.5 overflow-y-auto rounded-md border border-[color:var(--border-light)] bg-[color:var(--background-muted)] p-2.5">
                  {combos.map((c) => (
                    <div
                      key={`${c.size}-${c.color.en}`}
                      className={[
                        "flex items-center gap-1.5 rounded-[7px] px-2 py-1.5 text-[12.5px]",
                        c.exists
                          ? "border border-dashed border-[color:var(--border-light)] opacity-50"
                          : "border border-[color:var(--border-light)] bg-[color:var(--surface)]",
                      ].join(" ")}
                    >
                      <Swatch hex={c.color.hex} />
                      <span className="font-medium text-[color:var(--text-secondary)]">
                        {c.color.en}
                      </span>
                      <span className="text-[color:var(--text-muted)]">·</span>
                      <span className="font-mono font-semibold text-[color:var(--text-primary)]">
                        {c.size}
                      </span>
                      {c.exists ? (
                        <span className="ms-auto text-[11px] text-[color:var(--text-muted)]">
                          {t(keys.products.combosExistsTag)}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </Field>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-[color:var(--border-light)] px-5 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] text-[color:var(--text-muted)]">
              {footerNote}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={onClose}
                disabled={submitting}
              >
                {t(keys.common.cancel)}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={() => void submit()}
                disabled={!canSubmit}
                isLoading={submitting}
                leadingIcon={<PlusIcon />}
              >
                {submitLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalSheet>
  );
}

function chipClass(active: boolean) {
  return [
    "rounded-md border px-3 py-1.5 font-mono text-[13px] font-semibold transition-colors",
    active
      ? "border-[color:var(--accent-500)] bg-[color-mix(in_oklab,var(--accent-500)_12%,white)] text-[color:var(--accent-600)]"
      : "border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--background-muted)]",
  ].join(" ");
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="mb-1.5 block text-[12px] font-semibold uppercase tracking-[0.05em] text-[color:var(--text-muted)]">
        {label}
      </span>
      {children}
    </div>
  );
}

function SaleToggle({
  on,
  setOn,
  value,
  setValue,
  currency,
  t,
}: {
  on: boolean;
  setOn: (next: boolean) => void;
  value: string;
  setValue: (next: string) => void;
  currency: string;
  t: (key: string, values?: Record<string, string | number>) => string;
}) {
  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-[color:var(--border-light)] p-2.5">
      <label className="flex items-center gap-2">
        <ToggleInput
          name="sale-toggle"
          checked={on}
          onChange={(e) => setOn(e.target.checked)}
          ariaLabel={t(keys.products.variantOnSale)}
        />
        <span className="text-[13px] font-medium text-[color:var(--text-primary)]">
          {t(keys.products.variantOnSale)}
        </span>
      </label>
      {on ? (
        <Field label={`${t(keys.products.priceAfterDiscount)} (${currency})`}>
          <NumberInput
            name="discount"
            allowDecimal
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="—"
            className="font-mono font-semibold"
          />
        </Field>
      ) : null}
    </div>
  );
}

function Swatch({ hex }: { hex: string }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-[18px] w-[18px] shrink-0 rounded-full border border-black/10"
      style={{ background: HEX_RE.test(hex) ? hex : "#9ca3af" }}
    />
  );
}

function Tag({
  children,
  onRemove,
  removeLabel,
}: {
  children: React.ReactNode;
  onRemove: () => void;
  removeLabel: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border-light)] bg-[color:var(--surface)] py-1 ps-2 pe-1.5 text-[13px] text-[color:var(--text-primary)]">
      {children}
      <IconButton
        ariaLabel={removeLabel}
        icon={<CloseGlyph />}
        variant="ghost"
        size="xs"
        shape="round"
        onClick={onRemove}
        className="h-4 w-4"
      />
    </span>
  );
}

function ColorEditor({
  draft,
  onChange,
  enLabel,
  arLabel,
  hint,
}: {
  draft: ColorDraft;
  onChange: (d: ColorDraft) => void;
  enLabel: string;
  arLabel: string;
  hint: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={HEX_RE.test(draft.hex) ? draft.hex : "#888888"}
          onChange={(e) => onChange({ ...draft, hex: e.target.value })}
          className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] p-0"
          aria-label={hint}
        />
        <input
          value={draft.hex}
          onChange={(e) => onChange({ ...draft, hex: e.target.value })}
          placeholder="#RRGGBB"
          aria-label={hint}
          className="h-9 w-[110px] rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] px-2 font-mono text-[12.5px] focus:outline-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <TextInput
          name="color-en"
          value={draft.en}
          placeholder={enLabel}
          onChange={(e) => onChange({ ...draft, en: e.target.value })}
        />
        <TextInput
          name="color-ar"
          dir="rtl"
          value={draft.ar}
          placeholder={arLabel}
          onChange={(e) => onChange({ ...draft, ar: e.target.value })}
        />
      </div>
      <span className="text-[11px] text-[color:var(--text-muted)]">{hint}</span>
    </div>
  );
}
